import { useSuspenseQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { EditIcon, PlusIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/base/button";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";

import { DivisionForm } from "../controls/edit-divisions/division-form";
import { RemoveDivisionForm } from "../controls/edit-divisions/remove";

export function DivisionsForm({ tournamentId }: { tournamentId: number }) {
  const trpc = useTRPC();

  const { data: tournamentDivisions } = useSuspenseQuery(
    trpc.tournaments.getDivisionRequirements.queryOptions({ tournamentId }),
  );

  const [addingOrEditId, setAddingOrEditId] = useState<
    true | number | undefined
  >(undefined);

  return (
    <>
      {tournamentDivisions?.map((td) => (
        <div
          key={td.id}
          className="flex flex-col space-y-2 border-b border-gray-300 py-2 last-of-type:border-b-0"
        >
          <div className="flex w-full flex-row items-center justify-between">
            <div className="flex flex-col">
              <span
                className={clsx(td.id === addingOrEditId && "font-semibold")}
              >
                {getTournamentDivisionDisplay(td)}
              </span>
              {td.requirements.length > 0 && (
                <span className="text-xs text-gray-500">
                  {td.requirements
                    .map(
                      (r) =>
                        `${r.minimum ?? 1}× ${r.gender === "female" ? "F" : "M"} ${r.qualifiedDivision?.display ?? r.qualifiedDivision?.name?.toUpperCase() ?? "?"}`,
                    )
                    .join(", ")}
                </span>
              )}
            </div>

            <div className="flex flex-row gap-2">
              <Button
                size="sm"
                isDisabled={addingOrEditId !== undefined}
                onPress={() => {
                  setAddingOrEditId(td.id);
                }}
              >
                <EditIcon size={12} className="-mr" /> <span>Edit</span>
              </Button>

              <RemoveDivisionForm
                isDisabled={addingOrEditId !== undefined}
                tournamentId={tournamentId}
                divisionId={td.id}
              />
            </div>
          </div>

          {addingOrEditId === td.id && (
            <DivisionForm
              showTitle={false}
              tournamentId={tournamentId}
              divisionId={addingOrEditId}
              onCancel={() => setAddingOrEditId(undefined)}
            />
          )}
        </div>
      ))}

      {addingOrEditId === true ? (
        <DivisionForm
          tournamentId={tournamentId}
          onCancel={() => setAddingOrEditId(undefined)}
        />
      ) : (
        <Button
          size="sm"
          onPress={() => setAddingOrEditId(true)}
          isDisabled={addingOrEditId !== undefined}
        >
          <PlusIcon size={12} /> Add division
        </Button>
      )}
    </>
  );
}
